import Vue from 'vue';
import { CYCLE_MESSAGES, CYCLE_STAGES, FC_PROJECT_API } from '../utils/constants';

export default {
  namespaced: true,

  state: {
    projectList: [],
    projectListLoading: false,
    projectListError: null,
  },

  mutations: {
    setProjectList(state, projects) {
      state.projectList = projects;
    },
    setProjectListLoading(state, loading) {
      state.projectListLoading = loading;
    },
    setProjectListError(state, error) {
      state.projectListError = error;
    },
  },

  getters: {
    projectList: state => state.projectList,
    projectListLoading: state => state.projectListLoading,
    projectListError: state => state.projectListError,
  },

  actions: {
    async fetchProjectList({ commit }) {
      commit('setProjectListLoading', true);
      commit('setProjectListError', null);
      try {
        const response = await Vue.axios.post(FC_PROJECT_API.GET);
        if (response.data.status === 'success' && Array.isArray(response.data.data)) {
          commit('setProjectList', response.data.data);
        } else {
          commit('setProjectList', []);
          commit('setProjectListError', response.data.message || CYCLE_MESSAGES.ERROR.UNKNOWN);
        }
      } catch (err) {
        commit('setProjectList', []);
        commit('setProjectListError', err.message || CYCLE_MESSAGES.ERROR.UNKNOWN);
      } finally {
        commit('setProjectListLoading', false);
      }
    },

    /**
     * V3: Fetch project list with inline checklist summary.
     * Uses optimised single-aggregation endpoint.
     */
    async fetchProjectListV3({ commit }) {
      commit('setProjectListLoading', true);
      commit('setProjectListError', null);
      try {
        const response = await Vue.axios.post(FC_PROJECT_API.GET_V3);
        if (response.data.status === 'success' && Array.isArray(response.data.data)) {
          commit('setProjectList', response.data.data);
        } else {
          commit('setProjectList', []);
          commit('setProjectListError', response.data.message || CYCLE_MESSAGES.ERROR.UNKNOWN);
        }
      } catch (err) {
        commit('setProjectList', []);
        commit('setProjectListError', err.message || CYCLE_MESSAGES.ERROR.UNKNOWN);
      } finally {
        commit('setProjectListLoading', false);
      }
    },

    /**
     * Fetch the most recent open cycle (stage !== 3) sorted by end date descending.
     * Returns the cycle object or null if none found.
     */
    async fetchMostRecentOpenCycle() {
      try {
        const response = await Vue.axios.post(FC_PROJECT_API.GET);
        if (response.data.status === 'success' && Array.isArray(response.data.data)) {
          const openCycles = response.data.data.filter(c => c.stage !== CYCLE_STAGES.CLOSED && c.status === true);
          if (openCycles.length === 0) return null;
          openCycles.sort((a, b) => {
            const timeA = new Date(a.to).getTime() || 0;
            const timeB = new Date(b.to).getTime() || 0;
            return timeB - timeA;
          });
          return openCycles[0];
        }
        return null;
      } catch (err) {
        console.error('[project/store]: fetchMostRecentOpenCycle error', err);
        return null;
      }
    },

    async getFormData({ commit }) {
      try {
        const response = await Vue.axios.post(FC_PROJECT_API.GET_FORM_DATA);
        if (response.data.status === 'success') {
          return response.data.data[0] || {};
        }
        throw new Error(response.data.message || CYCLE_MESSAGES.ERROR.UNKNOWN);
      } catch (err) {
        console.error('[project/store]: getFormData error', err);
        commit('sbError', err.message || CYCLE_MESSAGES.ERROR.UNKNOWN, { root: true });
        throw err;
      }
    },

    async addProject({ commit }, payload) {
      try {
        const response = await Vue.axios.post(FC_PROJECT_API.ADD, { data: payload });
        return response;
      } catch (err) {
        console.error('[project/store]: addProject error', err);
        commit('sbError', err?.response?.data?.message || err.message || CYCLE_MESSAGES.ERROR.UNKNOWN, { root: true });
        throw err;
      }
    },

    async editProject({ commit }, { id, data }) {
      try {
        const response = await Vue.axios.post(FC_PROJECT_API.EDIT(id), { data });
        return response;
      } catch (err) {
        console.error('[project/store]: editProject error', err);
        commit('sbError', err.message || CYCLE_MESSAGES.ERROR.UNKNOWN, { root: true });
        throw err;
      }
    },

    async deleteProject({ commit }, id) {
      try {
        const response = await Vue.axios.post(FC_PROJECT_API.DELETE(id));
        if (response.data.status === 'success') {
          commit('sbSuccess', CYCLE_MESSAGES.SUCCESS.CYCLE_DELETED, { root: true });
        } else {
          throw new Error(response.data.message || CYCLE_MESSAGES.ERROR.UNKNOWN);
        }
        return response;
      } catch (err) {
        console.error('[project/store]: deleteProject error', err);
        commit('sbError', err.message || CYCLE_MESSAGES.ERROR.UNKNOWN, { root: true });
        throw err;
      }
    },

    async closeProject({ commit }, id) {
      try {
        const response = await Vue.axios.post(FC_PROJECT_API.CLOSE(id));
        if (response.data.status === 'success') {
          commit('sbSuccess', CYCLE_MESSAGES.SUCCESS.PROJECT_CLOSED, { root: true });
        } else {
          throw new Error(CYCLE_MESSAGES.ERROR.ERROR_CLOSING_PROJECT);
        }
        return response;
      } catch (err) {
        console.error('[project/store]: closeProject error', err);
        commit('sbError', err.message || CYCLE_MESSAGES.ERROR.UNKNOWN, { root: true });
        throw err;
      }
    },

    async openProject({ commit }, id) {
      try {
        const response = await Vue.axios.post(FC_PROJECT_API.OPEN(id));
        if (response.data.status === 'success') {
          commit('sbSuccess', CYCLE_MESSAGES.SUCCESS.PROJECT_REOPENED, { root: true });
        } else {
          throw new Error(CYCLE_MESSAGES.ERROR.ERROR_REOPENING_PROJECT);
        }
        return response;
      } catch (err) {
        console.error('[project/store]: openProject error', err);
        commit('sbError', err.message || CYCLE_MESSAGES.ERROR.UNKNOWN, { root: true });
        throw err;
      }
    },

    async toggleNotification({ commit }, { id, type }) {
      try {
        const api =
          type === 'Disable'
            ? FC_PROJECT_API.DISABLE_NOTIFICATION(id)
            : FC_PROJECT_API.ENABLE_NOTIFICATION(id);
        const response = await Vue.axios.post(api);
        if (response.data.status === 'success') {
          commit('sbSuccess', CYCLE_MESSAGES.SUCCESS.EMAIL_NOTIFICATION_SUCCESS(type), { root: true });
        } else {
          throw new Error(CYCLE_MESSAGES.ERROR.UNKNOWN);
        }
        return response;
      } catch (err) {
        console.error('[project/store]: toggleNotification error', err);
        commit('sbError', err.message || CYCLE_MESSAGES.ERROR.UNKNOWN, { root: true });
        throw err;
      }
    },

    async generateAIChecklist({ commit }, payload) {
      try {
        const response = await Vue.axios.post(FC_PROJECT_API.GENERATE_AI_CHECKLIST, {
          data: payload,
        });
        return response;
      } catch (err) {
        console.error('[project/store]: generateAIChecklist error', err);
        commit('sbError', err.message || CYCLE_MESSAGES.ERROR.UNKNOWN, { root: true });
        throw err;
      }
    },

    async syncERP({ commit }, { payload, id }) {
      try {
        if (!id || !payload.from || !payload.to) {
          throw new Error('Missing project or period dates');
        }
        const body = { project: id, startDate: payload.from, endDate: payload.to };
        const response = await Vue.axios.post(FC_PROJECT_API.ERP_SYNC_TRIAL_BALANCE, body);
        return response;
      } catch (err) {
        console.error('[project/store]: syncERP error', err);
        commit('sbError', err.message || CYCLE_MESSAGES.ERROR.UNKNOWN, { root: true });
        throw err;
      }
    },

    async checkChartOfAccountsSetup({ commit }) {
      try {
        const response = await Vue.axios.post(FC_PROJECT_API.CHART_OF_ACCOUNTS_GET);
        if (response.data.status === 'success') {
          return response.data.data.length > 0;
        }
        return false;
      } catch (err) {
        console.error('[project/store]: checkChartOfAccountsSetup error', err);
        commit('sbError', err.message || CYCLE_MESSAGES.ERROR.UNKNOWN, { root: true });
        return false;
      }
    },

    async retryCycleCreation({ commit }, id) {
      try {
        const response = await Vue.axios.post(FC_PROJECT_API.RETRY_CYCLE_CREATION(id));
        if (response.data.status === 'success') {
          commit('sbSuccess', CYCLE_MESSAGES.SUCCESS.CYCLE_CREATION_RETRY, { root: true });
        } else {
          throw new Error(response.data.message || CYCLE_MESSAGES.ERROR.UNKNOWN);
        }
        return response;
      } catch (err) {
        console.error('[project/store]: retryCycleCreation error', err);
        commit('sbError', err.message || CYCLE_MESSAGES.ERROR.UNKNOWN, { root: true });
        throw err;
      }
    },
  },
};
